// =============================================================================
// セーブ/ロード（localStorage）。プレイヤー位置・ステータス・インベントリ・時間・
// 採取ノードの残量を保存。海/カメラには無関係（ゲーム状態のみ）。
// =============================================================================
import { stats } from './survival';
import { inventory } from './crafting';
import { clock } from './time';
import type { Player, ResourceNode, Stats } from './types';

const KEY = 'side6-save-v1';

export interface SaveData {
  v: number;
  savedAt: number;
  player: { pos: { x: number; y: number; z: number }; yaw: number; pitch: number; eyeHeight?: number };
  stats: Stats;
  inv: Record<string, number>;
  clock: { t: number; day: number };
  nodes: number[]; // index順の残量
  repair?: Record<string, number>; // 難破船の修理進捗
  placed?: { type: string; x: number; y: number; z: number; water: number }[]; // 設置物(焚き火/給水器)
}

export function saveState(player: Player, nodes: ResourceNode[], repair?: Record<string, number>, placed?: { type: string; x: number; y: number; z: number; water: number }[]): boolean {
  const data: SaveData = {
    v: 1,
    savedAt: Date.now(),
    player: { pos: { ...player.pos }, yaw: player.yaw, pitch: player.pitch, eyeHeight: player.eyeHeight },
    stats: { ...stats },
    inv: { ...inventory },
    clock: { t: clock.t, day: clock.day },
    nodes: nodes.map((n) => n.amount),
    repair: repair ? { ...repair } : undefined,
    placed: placed ? placed.map((p) => ({ ...p })) : undefined,
  };
  try { localStorage.setItem(KEY, JSON.stringify(data)); return true; } catch { return false; }
}

// ロード時の形状検証。壊れたセーブ（手書き改変・部分書き込み・旧形式の残骸）を
// 「セーブなし」に落とし、applyState/restorePlaced でのクラッシュ・NaN混入・
// tickTime の Infinity 無限ループを防ぐ（不正時はタイトルが「はじめる」のみになり安全に新規開始）。
const isNum = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v);
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

function isValidSave(d: any): d is SaveData {
  if (!isObj(d) || d.v !== 1) return false;
  const p = d.player as any;
  if (!isObj(p) || !isObj(p.pos) || !isNum(p.pos.x) || !isNum(p.pos.y) || !isNum(p.pos.z)) return false;
  if (!isNum(p.yaw) || !isNum(p.pitch)) return false;
  if (p.eyeHeight !== undefined && !isNum(p.eyeHeight)) return false;
  if (!isObj(d.stats) || !isNum((d.stats as any).health)) return false;
  for (const v of Object.values(d.stats)) if (typeof v !== 'boolean' && !isNum(v)) return false;
  if (!isObj(d.inv)) return false;
  for (const v of Object.values(d.inv)) if (!isNum(v)) return false;
  if (!isObj(d.clock) || !isNum((d.clock as any).t) || !isNum((d.clock as any).day)) return false;
  if (!Array.isArray(d.nodes) || !d.nodes.every(isNum)) return false;
  if (d.repair !== undefined) {
    if (!isObj(d.repair)) return false;
    for (const v of Object.values(d.repair)) if (!isNum(v)) return false;
  }
  if (d.placed !== undefined) {
    if (!Array.isArray(d.placed)) return false;
    for (const q of d.placed as any[]) {
      if (!isObj(q) || typeof q.type !== 'string' || !isNum(q.x) || !isNum(q.y) || !isNum(q.z)) return false;
      if (q.water !== undefined && !isNum(q.water)) return false;
    }
  }
  return true;
}

export function loadState(): SaveData | null {
  try {
    const s = localStorage.getItem(KEY);
    if (!s) return null;
    const d = JSON.parse(s) as SaveData;
    return isValidSave(d) ? d : null;
  } catch { return null; }
}

export function clearSave(): void {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

export function hasSave(): boolean { return loadState() != null; }

// ロードしたデータを現在の state に適用する
export function applyState(d: SaveData, player: Player, nodes: ResourceNode[], repair?: Record<string, number>): void {
  player.pos.x = d.player.pos.x; player.pos.y = d.player.pos.y; player.pos.z = d.player.pos.z;
  player.yaw = d.player.yaw; player.pitch = d.player.pitch; player.vy = 0;
  if (d.player.eyeHeight) player.eyeHeight = d.player.eyeHeight;
  Object.assign(stats, d.stats);
  for (const k of Object.keys(inventory)) delete inventory[k];
  Object.assign(inventory, d.inv);
  clock.t = d.clock.t; clock.day = d.clock.day;
  for (let i = 0; i < nodes.length && i < d.nodes.length; i++) nodes[i].amount = d.nodes[i];
  if (repair && d.repair) { for (const k of Object.keys(repair)) delete repair[k]; Object.assign(repair, d.repair); }
}
